import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Papa from 'papaparse';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Step1Template, type Step1Data } from './Step1Template';
import { Step2Recipients, type Step2Data } from './Step2Recipients';
import { Step3Review, type Step3Data } from './Step3Review';
import { campaignsApi } from '@/api/campaigns.api';
import { contactsApi } from '@/api/contacts.api';
import { extractErrorMessage } from '@/lib/errors';
import type { Template } from '@/types/api.types';

type WizardData = Step1Data & Step2Data & Step3Data;

const INITIAL: WizardData = {
  name: '',
  templateId: '',
  template: null as Template | null,
  fieldMappings: {},
  recipientMode: 'csv',
  csvFile: null,
  csvPhones: [],
  csvParsedHeaders: [],
  csvParsedRows: [],
  phoneCol: '',
  nameCol: '',
  selectedContactIds: [],
  scheduledAt: '',
};

const STEPS = ['Template', 'Destinatários', 'Revisão'];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CampaignWizard({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [data, setData] = useState<WizardData>(INITIAL);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function patch(p: Partial<WizardData>) {
    setData((d) => ({ ...d, ...p }));
  }

  function handleClose() {
    onOpenChange(false);
    setTimeout(() => { setStep(1); setData(INITIAL); }, 300);
  }

  async function handleSubmit() {
    setIsSubmitting(true);
    try {
      let contactIds: string[];

      if (data.recipientMode === 'csv' && data.csvFile) {
        // Remap headers and import contacts
        const newHeaders = data.csvParsedHeaders.map((h, i) => {
          if (h === data.phoneCol) return 'phone';
          if (h === data.nameCol) return 'name';
          return `col_${i}`;
        });
        const csvText = Papa.unparse([newHeaders, ...data.csvParsedRows]);
        const file = new File([csvText], 'import.csv', { type: 'text/csv' });
        await contactsApi.importCsv(file);

        // Fetch all contacts and match by phone
        const res = await contactsApi.list({ pageSize: 500 });
        const all = res.data.data;
        contactIds = all.filter((c) => data.csvPhones.includes(c.phone)).map((c) => c.id);

        if (contactIds.length === 0) {
          toast.error('Nenhum contato importado encontrado. Verifique o formato dos telefones (E.164).');
          return;
        }
      } else {
        contactIds = data.selectedContactIds;
      }

      const res = await campaignsApi.create({
        name: data.name,
        templateId: data.templateId,
        contactIds,
        fieldMappings: Object.keys(data.fieldMappings).length > 0 ? data.fieldMappings : undefined,
        scheduledAt: data.scheduledAt ? new Date(data.scheduledAt).toISOString() : undefined,
      });

      await qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campanha criada!');
      handleClose();
      navigate(`/campaigns/${res.data.data.id}`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nova campanha</DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-0">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const active = n === step;
            const done = n < step;
            return (
              <div key={label} className="flex flex-1 items-center">
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                  active ? 'bg-primary text-primary-foreground'
                    : done ? 'bg-primary/20 text-primary'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {n}
                </div>
                <span className={`ml-1.5 text-xs ${active ? 'font-medium' : 'text-muted-foreground'}`}>
                  {label}
                </span>
                {i < STEPS.length - 1 && (
                  <div className="mx-2 h-px flex-1 bg-border" />
                )}
              </div>
            );
          })}
        </div>

        <div className="pt-2">
          {step === 1 && (
            <Step1Template
              data={data}
              onChange={patch}
              onNext={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <Step2Recipients
              data={data}
              onChange={patch}
              onNext={() => setStep(3)}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && (
            <Step3Review
              data={data}
              onChange={patch}
              onBack={() => setStep(2)}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
